import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSearch, FiFilter, FiInstagram, FiFacebook, FiUser, FiCheck, FiX, FiTrash2, FiClock, FiDownload, FiFileText, FiPlus, FiMinus } from 'react-icons/fi';
import { SiTiktok } from 'react-icons/si';
import Swal from 'sweetalert2';
import { PDFDocument, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import { adminApi } from '../../utils/api';
import { extractSocialUsername, buildSocialProfileUrl } from '../../utils/socialUsername';
import './AdminPages.css';

// Category options mapping
const categoryLabels = {
  fashion: 'אופנה',
  beauty: 'יופי',
  lifestyle: 'לייפסטייל',
  food: 'אוכל',
  travel: 'טיולים',
  fitness: 'כושר',
  tech: 'טכנולוגיה',
  gaming: 'גיימינג',
  parenting: 'הורות',
  business: 'עסקים',
  art: 'אמנות',
  music: 'מוזיקה',
  comedy: 'קומדיה',
  education: 'חינוך',
  pets: 'חיות מחמד'
};

const platformLabels = {
  instagram: 'אינסטגרם',
  tiktok: 'טיקטוק',
  facebook: 'פייסבוק'
};

const AdminCreatorsPage = ({ filter }) => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ city: '', gender: '' });
  const [showFilters, setShowFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const queryClient = useQueryClient();

  const toggleRowExpand = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const { data, isLoading, error } = useQuery(
    ['admin-creators', page, search, filters],
    () => adminApi.getCreators({ page, limit: 100, search, ...filters }),
    { keepPreviousData: true }
  );

  const approveMutation = useMutation(
    (id) => adminApi.approveCreator(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-creators');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        toast.success('המשפיען אושר בהצלחה');
      },
      onError: () => {
        toast.error('שגיאה באישור המשפיען');
      }
    }
  );

  const rejectMutation = useMutation(
    (id) => adminApi.rejectCreator(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-creators');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        toast.success('המשפיען נדחה');
      },
      onError: () => {
        toast.error('שגיאה בדחיית המשפיען');
      }
    }
  );

  const pendingMutation = useMutation(
    (id) => adminApi.pendingCreator(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-creators');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        toast.success('המשפיען הוחזר לממתין');
      },
      onError: () => {
        toast.error('שגיאה בהחזרת המשפיען לממתין');
      }
    }
  );

  const deleteMutation = useMutation(
    (id) => adminApi.deleteCreator(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('admin-creators');
        queryClient.invalidateQueries('admin-stats');
        queryClient.invalidateQueries('user-stats');
        toast.success('המשפיען נמחק בהצלחה');
      },
      onError: () => {
        toast.error('שגיאה במחיקת המשפיען');
      }
    }
  );

  const creators = data?.data?.creators || [];
  const pagination = data?.data?.pagination || {};

  // Split creators into approved and pending
  const approvedCreators = creators.filter(c => c.status === 'approved');
  const pendingCreators = creators.filter(c => c.status === 'pending');

  // Determine which creators to show based on filter prop
  const showPending = !filter || filter === 'pending';
  const showApproved = !filter || filter === 'approved';

  // Get page title based on filter
  const getPageTitle = () => {
    if (filter === 'pending') return 'ממתינים לאישור';
    if (filter === 'approved') return 'מאושרים';
    return 'פרופילים (משפיענים)';
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('he-IL');
  };

  const handleApprove = (id) => {
    approveMutation.mutate(id);
  };

  const handlePending = async (id) => {
    const result = await Swal.fire({
      title: 'החזרה לממתין',
      text: 'האם אתה בטוח שברצונך להחזיר את המשפיען לממתין?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#f0ad4e',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'כן, החזר לממתין',
      cancelButtonText: 'ביטול',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      pendingMutation.mutate(id);
    }
  };

  const handleReject = async (id) => {
    const result = await Swal.fire({
      title: 'ביטול אישור',
      text: 'האם אתה בטוח שברצונך לבטל את האישור?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#F7941D',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'כן, בטל אישור',
      cancelButtonText: 'ביטול',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      rejectMutation.mutate(id);
    }
  };

  const handleDelete = async (id, name) => {
    const result = await Swal.fire({
      title: 'מחיקת משפיען',
      html: `האם אתה בטוח שברצונך למחוק את המשפיען <strong>"${name}"</strong>?<br/><br/><span style="color: #dc3545;">פעולה זו לא ניתנת לביטול.</span>`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'כן, מחק',
      cancelButtonText: 'ביטול',
      reverseButtons: true
    });

    if (result.isConfirmed) {
      deleteMutation.mutate(id);
    }
  };

  // Download signed agreement PDF with details and signature
  const downloadSignedAgreement = async (creator) => {
    const prepToast = toast.info('מכין את ההסכם להורדה...', { autoClose: false });
    try {
      const pdfResponse = await fetch('/CustomerAgreement.pdf', { cache: 'no-store' });
      if (!pdfResponse.ok) {
        throw new Error(`PDF base file not found (${pdfResponse.status})`);
      }
      const pdfArrayBuffer = await pdfResponse.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfArrayBuffer);

      pdfDoc.registerFontkit(fontkit);

      const fontResponse = await fetch('/Assistant-Bold.ttf', { cache: 'no-store' });
      if (!fontResponse.ok) {
        throw new Error(`Hebrew font not found (${fontResponse.status})`);
      }
      const fontBytes = await fontResponse.arrayBuffer();
      const hebrewFont = await pdfDoc.embedFont(fontBytes);

      const pages = pdfDoc.getPages();
      const firstPage = pages[0];
      const secondPage = pages[1];

      const textColor = rgb(0, 0, 0);
      const fontSize = 11;

      const drawText = (page, text, x, y, customSize) => {
        if (!text) return;
        const cleanText = String(text).trim();
        try {
          page.drawText(cleanText, { x, y, size: customSize || fontSize, font: hebrewFont, color: textColor });
        } catch (err) {
          console.error('Error drawing text:', err);
        }
      };

      const dateObj = creator.agreement_signed_at ? new Date(creator.agreement_signed_at) : new Date();
      const signedDate = `${dateObj.getDate()}.${dateObj.getMonth() + 1}.${dateObj.getFullYear()}`;

      drawText(firstPage, signedDate, 416, 663);
      drawText(firstPage, creator.agreement_full_name || creator.full_name || creator.display_name, 516, 595);
      drawText(firstPage, creator.agreement_id_number, 369, 595);
      drawText(firstPage, creator.agreement_invoice_email || creator.email, 441, 560, 8);
      drawText(firstPage, creator.agreement_contact_phone || creator.phone, 322, 561);

      if (creator.agreement_signature && secondPage) {
        try {
          const sig = creator.agreement_signature;
          // Decode data URL directly (avoid fetch on giant data URLs which fails in some browsers)
          let signatureBytes;
          if (typeof sig === 'string' && sig.startsWith('data:')) {
            const base64 = sig.split(',')[1] || '';
            const binary = atob(base64);
            signatureBytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i++) signatureBytes[i] = binary.charCodeAt(i);
          } else {
            signatureBytes = await fetch(sig).then(res => res.arrayBuffer());
          }
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          secondPage.drawImage(signatureImage, { x: 238, y: 230, width: 120, height: 45 });
        } catch (sigError) {
          console.error('Error embedding signature:', sigError);
        }
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);

      // Trigger download via anchor (popup blockers block window.open of blob URLs)
      const filename = `agreement-${(creator.agreement_full_name || creator.full_name || creator.display_name || 'creator').replace(/[^\w֐-׿\-]/g, '_')}.pdf`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60_000);

      toast.dismiss(prepToast);
      toast.success('ההסכם הורד בהצלחה');
    } catch (error) {
      console.error('Error generating agreement:', error);
      toast.dismiss(prepToast);
      toast.error(`שגיאה בהורדת ההסכם: ${error.message}`);
    }
  };

  // Parse categories from JSON or comma-separated string
  const parseCategories = (categories) => {
    if (!categories) return [];
    if (Array.isArray(categories)) return categories;
    try {
      return JSON.parse(categories);
    } catch {
      return categories.split(',').map(c => c.trim()).filter(Boolean);
    }
  };

  // Helper function to show social link popup with auto-detection of platform
  const showSocialLinkPopup = (rawValue, creator, expectedPlatform) => {
    const isUrl = rawValue.startsWith('http');
    const creatorName = creator.display_name || creator.full_name || '-';
    const creatorEmail = creator.email || '-';

    // Detect actual platform from URL
    let detectedPlatform = expectedPlatform;
    if (isUrl) {
      if (rawValue.includes('instagram.com')) detectedPlatform = 'instagram';
      else if (rawValue.includes('tiktok.com')) detectedPlatform = 'tiktok';
      else if (rawValue.includes('facebook.com')) detectedPlatform = 'facebook';
    }

    // Platform configurations
    const platformConfig = {
      instagram: {
        name: 'אינסטגרם',
        gradient: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
        color: '#E4405F',
        urlPattern: /^https?:\/\/(www\.)?instagram\.com\/[^\s?\/]+/,
        usernamePattern: /instagram\.com\/([^\s?\/]+)/,
        buildUrl: (username) => `https://instagram.com/${username.replace('@', '')}`
      },
      tiktok: {
        name: 'טיקטוק',
        gradient: 'linear-gradient(45deg, #00f2ea, #ff0050)',
        color: '#000000',
        urlPattern: /^https?:\/\/(www\.)?tiktok\.com\/@?[^\s?\/]+/,
        usernamePattern: /tiktok\.com\/@?([^\s?\/]+)/,
        buildUrl: (username) => `https://tiktok.com/@${username.replace('@', '')}`
      },
      facebook: {
        name: 'פייסבוק',
        gradient: 'linear-gradient(45deg, #1877f2, #3b5998)',
        color: '#1877f2',
        urlPattern: /^https?:\/\/(www\.)?facebook\.com\/[^\s?\/]+/,
        usernamePattern: /facebook\.com\/([^\s?\/]+)/,
        buildUrl: (username) => `https://facebook.com/${username}`
      }
    };

    // Use shared extractor — handles raw usernames, URLs with tracking params, etc.
    const extractedUsername = extractSocialUsername(detectedPlatform, rawValue);
    const isValidUrl = !!extractedUsername;
    const url = extractedUsername
      ? buildSocialProfileUrl(detectedPlatform, extractedUsername)
      : rawValue;
    const config = platformConfig[detectedPlatform] || platformConfig.instagram;

    Swal.fire({
      title: config.name,
      html: isValidUrl ? `
        <div style="text-align: right; direction: rtl; padding: 10px 0;">
          <div style="background: ${config.gradient}; padding: 15px; border-radius: 10px; margin-bottom: 15px;">
            <div style="display: flex; align-items: center; justify-content: flex-end; gap: 12px; color: white;">
              <div style="text-align: right;">
                <div style="font-weight: bold; font-size: 16px;">@${extractedUsername}</div>
                <div style="font-size: 13px; opacity: 0.9;">${creatorName}</div>
              </div>
              <div style="width: 50px; height: 50px; border-radius: 50%; background: white; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                ${creator.profile_image
                  ? `<img src="${creator.profile_image}" style="width: 100%; height: 100%; object-fit: cover;" />`
                  : `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${config.color}" stroke-width="2"><circle cx="12" cy="8" r="4"/><path d="M20 21a8 8 0 1 0-16 0"/></svg>`
                }
              </div>
            </div>
          </div>
          <div style="background: #f5f5f5; padding: 10px; border-radius: 8px; margin-bottom: 10px;">
            <div style="font-size: 12px; color: #666; margin-bottom: 5px;">פרטי יוצר במערכת:</div>
            <div style="font-size: 14px;"><strong>שם:</strong> ${creatorName}</div>
            <div style="font-size: 14px;"><strong>אימייל:</strong> ${creatorEmail}</div>
          </div>
          <div style="margin-top: 10px;">
            <a href="${url}" target="_blank" rel="noopener noreferrer" style="color: ${config.color}; word-break: break-all; font-size: 14px;">${url}</a>
          </div>
        </div>
      ` : `
        <div style="text-align: center; padding: 20px;">
          <div style="color: #dc3545; font-size: 18px; font-weight: bold; margin-bottom: 10px;">⚠️ קישור לא תקין</div>
          <div style="color: #666; font-size: 14px; direction: rtl;">הקישור שהוזן אינו בפורמט תקין</div>
          <div style="background: #f5f5f5; padding: 10px; border-radius: 8px; margin-top: 15px; word-break: break-all; color: #999;">${rawValue}</div>
        </div>
      `,
      confirmButtonText: isValidUrl ? 'פתח בחלון חדש' : 'סגור',
      confirmButtonColor: config.color,
      showCancelButton: isValidUrl,
      cancelButtonText: 'סגור',
      width: 450,
    }).then((result) => {
      if (result.isConfirmed && isValidUrl) {
        window.open(url, '_blank');
      }
    });
  };

  const renderCreatorRow = (creator, showApproveButton = true) => {
    const isExpanded = expandedRows[creator.id];
    const categories = parseCategories(creator.categories);
    const customCategories = parseCategories(creator.custom_categories);
    const platforms = parseCategories(creator.preferred_platforms);

    return (
      <React.Fragment key={creator.id}>
        <tr className={isExpanded ? 'expanded-parent' : ''}>
          <td data-label="">
            <button
              className="btn-expand"
              onClick={() => toggleRowExpand(creator.id)}
              title={isExpanded ? 'סגור פרטים' : 'הצג פרטים נוספים'}
            >
              {isExpanded ? <FiMinus /> : <FiPlus />}
            </button>
          </td>
          <td data-label="שם">
            <div className="user-cell">
              <div className="user-avatar-small">
                {creator.profile_image ? (
                  <img src={creator.profile_image} alt={creator.display_name} />
                ) : (
                  <FiUser />
                )}
              </div>
              <span>{creator.display_name || creator.full_name || '-'}</span>
            </div>
          </td>
      <td data-label="אימייל">
        <a href={`mailto:${creator.email}`} className="email-link">
          {creator.email}
        </a>
      </td>
      <td data-label="טלפון">
        {creator.phone ? (
          <a href={`tel:${creator.phone}`} className="phone-link">
            {creator.phone}
          </a>
        ) : '-'}
      </td>
      <td data-label="עיר">{creator.city || '-'}</td>
      <td data-label="מגדר">
        {creator.gender === 'male' ? 'זכר' :
         creator.gender === 'female' ? 'נקבה' :
         creator.gender === 'other' ? 'אחר' : '-'}
      </td>
      <td data-label="שנת לידה">{creator.birth_year || '-'}</td>
      <td data-label="תאריך הרשמה">{formatDate(creator.created_at)}</td>
      <td data-label="סטטוס">
        <div className="approval-actions">
          {creator.status === 'approved' ? (
            <>
              <span className="badge badge-success">
                <FiCheck /> מאושר
              </span>
              <button
                className="btn-icon btn-warning small"
                onClick={() => handlePending(creator.id)}
                title="החזר לממתין"
                style={{ background: '#f0ad4e', color: '#fff', border: 'none' }}
              >
                <FiClock />
              </button>
              <button
                className="btn-icon btn-danger small"
                onClick={() => handleReject(creator.id)}
                title="בטל אישור"
              >
                <FiX />
              </button>
            </>
          ) : creator.status === 'rejected' ? (
            <div className="status-buttons-vertical">
              {showApproveButton && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleApprove(creator.id)}
                >
                  <FiCheck /> אשר
                </button>
              )}
              <span className="status-label rejected">
                <FiX /> נדחה
              </span>
            </div>
          ) : (
            <div className="status-buttons-vertical">
              {showApproveButton && (
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => handleApprove(creator.id)}
                >
                  <FiCheck /> אשר
                </button>
              )}
              <span className="status-label pending">
                <FiClock /> ממתין
              </span>
            </div>
          )}
        </div>
      </td>
      <td data-label="נחתם הסכם">
        {creator.agreement_signed ? (
          <div className="agreement-status signed">
            <span className="badge badge-success">
              <FiCheck /> נחתם
            </span>
            {creator.agreement_signature && (
              <button
                className="btn-icon btn-outline small"
                onClick={() => downloadSignedAgreement(creator)}
                title="הורד הסכם חתום"
              >
                <FiDownload />
              </button>
            )}
          </div>
        ) : (
          <span className="badge badge-warning">
            <FiFileText /> לא נחתם
          </span>
        )}
      </td>
      <td data-label="מחיקה">
        <button
          className="btn-icon btn-danger small"
          onClick={() => handleDelete(creator.id, creator.display_name || creator.full_name || creator.email)}
          title="מחק משפיען"
          disabled={deleteMutation.isLoading}
        >
          <FiTrash2 />
        </button>
      </td>
    </tr>
    <AnimatePresence>
      {isExpanded && (
        <motion.tr
          className="expanded-row"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <td colSpan="11">
            <div className="expanded-content">
              <div className="expanded-section">
                <h4>רשתות חברתיות</h4>
                <div className="social-buttons">
                  {creator.instagram_username ? (
                    <button
                      className="btn btn-social instagram"
                      onClick={() => showSocialLinkPopup(creator.instagram_username, creator, 'instagram')}
                    >
                      <FiInstagram /> אינסטגרם
                    </button>
                  ) : (
                    <span className="btn btn-social disabled">
                      <FiInstagram /> אין אינסטגרם
                    </span>
                  )}
                  {creator.tiktok_username ? (
                    <button
                      className="btn btn-social tiktok"
                      onClick={() => showSocialLinkPopup(creator.tiktok_username, creator, 'tiktok')}
                    >
                      <SiTiktok /> טיקטוק
                    </button>
                  ) : (
                    <span className="btn btn-social disabled">
                      <SiTiktok /> אין טיקטוק
                    </span>
                  )}
                  {creator.facebook_username ? (
                    <button
                      className="btn btn-social facebook"
                      onClick={() => showSocialLinkPopup(creator.facebook_username, creator, 'facebook')}
                    >
                      <FiFacebook /> פייסבוק
                    </button>
                  ) : (
                    <span className="btn btn-social disabled">
                      <FiFacebook /> אין פייסבוק
                    </span>
                  )}
                </div>
              </div>
              <div className="expanded-section">
                <h4>תחומי עניין</h4>
                <div className="tags-list">
                  {categories.length > 0 ? (
                    categories.map(cat => (
                      <span key={cat} className="tag tag-category">
                        {categoryLabels[cat] || cat}
                      </span>
                    ))
                  ) : (
                    <span className="no-data">לא צוינו</span>
                  )}
                  {customCategories.map(cat => (
                    <span key={cat} className="tag tag-custom">
                      {cat}
                    </span>
                  ))}
                </div>
              </div>
              <div className="expanded-section">
                <h4>פלטפורמות</h4>
                <div className="tags-list">
                  {platforms.length > 0 ? (
                    platforms.map(plat => (
                      <span key={plat} className="tag tag-platform">
                        {platformLabels[plat] || plat}
                      </span>
                    ))
                  ) : (
                    <span className="no-data">לא צוינו</span>
                  )}
                </div>
              </div>
            </div>
          </td>
        </motion.tr>
      )}
    </AnimatePresence>
    </React.Fragment>
    );
  };

  const TableHeader = () => (
    <thead>
      <tr>
        <th style={{ width: '40px' }}></th>
        <th>שם</th>
        <th>אימייל</th>
        <th>טלפון</th>
        <th>עיר</th>
        <th>מגדר</th>
        <th>שנת לידה</th>
        <th>תאריך הרשמה</th>
        <th>סטטוס</th>
        <th>נחתם הסכם</th>
        <th>מחיקה</th>
      </tr>
    </thead>
  );

  return (
    <div className="admin-page">
      <header className="admin-header">
        <div className="header-content">
          <h1>{getPageTitle()}</h1>
          <span className="total-count">
            {filter === 'pending' ? pendingCreators.length :
             filter === 'approved' ? approvedCreators.length :
             pagination.total || 0} משפיענים
          </span>
        </div>
      </header>

      {/* Search and Filters */}
      <div className="admin-toolbar">
        <form onSubmit={handleSearch} className="search-form">
          <div className="search-input-wrapper">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="חיפוש לפי שם, אימייל, טלפון..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <button type="submit" className="btn btn-primary">חפש</button>
        </form>

        <button
          className={`btn btn-outline filter-btn ${showFilters ? 'active' : ''}`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <FiFilter /> סינון
        </button>
      </div>

      {showFilters && (
        <motion.div
          className="filters-panel"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
        >
          <div className="filter-group">
            <label>עיר</label>
            <input
              type="text"
              placeholder="סנן לפי עיר"
              value={filters.city}
              onChange={(e) => setFilters({ ...filters, city: e.target.value })}
            />
          </div>
          <div className="filter-group">
            <label>מגדר</label>
            <select
              value={filters.gender}
              onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
            >
              <option value="">הכל</option>
              <option value="male">זכר</option>
              <option value="female">נקבה</option>
              <option value="other">אחר</option>
            </select>
          </div>
          <button
            className="btn btn-outline"
            onClick={() => {
              setFilters({ city: '', gender: '' });
              setPage(1);
            }}
          >
            נקה סינון
          </button>
        </motion.div>
      )}

      {/* Content */}
      {isLoading ? (
        <div className="loading-state">
          <div className="spinner" />
          <p>טוען נתונים...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p>שגיאה בטעינת הנתונים</p>
        </div>
      ) : creators.length === 0 ? (
        <div className="empty-state">
          <FiUser className="empty-icon" />
          <p>לא נמצאו משפיענים</p>
        </div>
      ) : (
        <>
          {/* Pending/Rejected Creators Table */}
          {showPending && pendingCreators.length > 0 && (
            <div className="table-section">
              {!filter && (
                <h2 className="table-section-title">
                  <FiClock /> ממתינים לאישור ({pendingCreators.length})
                </h2>
              )}
              <div className="table-container">
                <table className="admin-table">
                  <TableHeader />
                  <tbody>
                    {pendingCreators.map((creator) => renderCreatorRow(creator, true))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Show empty state for pending filter */}
          {filter === 'pending' && pendingCreators.length === 0 && (
            <div className="empty-state">
              <FiClock className="empty-icon" />
              <p>אין משפיענים ממתינים לאישור</p>
            </div>
          )}

          {/* Approved Creators Table */}
          {showApproved && approvedCreators.length > 0 && (
            <div className="table-section">
              {!filter && (
                <h2 className="table-section-title approved">
                  <FiCheck /> מאושרים ({approvedCreators.length})
                </h2>
              )}
              <div className="table-container">
                <table className="admin-table">
                  <TableHeader />
                  <tbody>
                    {approvedCreators.map((creator) => renderCreatorRow(creator, false))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Show empty state for approved filter */}
          {filter === 'approved' && approvedCreators.length === 0 && (
            <div className="empty-state">
              <FiCheck className="empty-icon" />
              <p>אין משפיענים מאושרים</p>
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <button
            className="btn btn-outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            הקודם
          </button>
          <span className="page-info">
            עמוד {page} מתוך {pagination.pages}
          </span>
          <button
            className="btn btn-outline"
            disabled={page === pagination.pages}
            onClick={() => setPage(page + 1)}
          >
            הבא
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminCreatorsPage;
